import { useEffect, useMemo, useState } from "react";
import { ArrowLeftRight, Info, Calendar as CalendarIcon } from "lucide-react";
import api from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { Disclaimer } from "@/components/Shared";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

function InfoPopover({ title, children, width = "w-80", testid }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`About ${title}`}
          data-testid={testid}
          className="inline-flex items-center justify-center text-slate-400 hover:text-gold transition-colors align-middle ml-1"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className={`${width} max-h-[70vh] overflow-y-auto text-left`}>
        <p className="font-display font-semibold text-navy text-sm mb-2">{title}</p>
        <div className="text-xs leading-relaxed text-slate-600 space-y-2">{children}</div>
        <p className="mt-3 pt-2 border-t border-border text-[10px] text-slate-400 italic">Educational content only. Not investment advice.</p>
      </PopoverContent>
    </Popover>
  );
}

function BiasInfo() {
  return (
    <InfoPopover title="How is Bullish / Bearish calculated?" width="w-96" testid="oi-bias-info">
      <p>Whether OI is bullish or bearish is calculated by combining the following:</p>
      <ol className="list-decimal pl-4 space-y-2">
        <li><strong>Outstanding OI at end of day</strong> — Positive OI in calls is bullish, negative OI in calls is bearish, positive OI in puts is bearish, and negative OI in puts is bullish.</li>
        <li><strong>Change in OI</strong> from previous day to current day — Increase in calls is bullish, decrease in calls is bearish, increase in puts is bearish, and decrease in puts is bullish.</li>
      </ol>
    </InfoPopover>
  );
}

function ParticipantInfo() {
  return (
    <InfoPopover title="How to read participant data?" width="w-[420px]" testid="oi-participant-info">
      <p className="font-semibold text-navy">Definitions</p>
      <ol className="list-decimal pl-4 space-y-2">
        <li><strong>FII</strong> — Foreign Institutional Investors (prop firms, hedge funds, investment banks, pension funds). Sophisticated investors, right most of the time, especially in F&O. If FII is bullish, market is expected to go up; if bearish, down.</li>
        <li><strong>Pro</strong> — Trading firms with broking membership trading their own funds (incl. foreign funds and high-frequency desks). If Pro is bullish, market is expected to go up; if bearish, down.</li>
        <li><strong>Client</strong> — All trades done through brokers. A common misconception is this is purely retail — it also includes HNIs, small funds, companies and trading desks. Retail is wrong most of the time in F&O, so if they are bullish the market is expected to go down, and if bearish, up.</li>
        <li><strong>DII</strong> — Domestic Institutional Investors, mainly AMCs investing in mutual funds. Not very active in F&O; this data is not very useful for trading F&O.</li>
      </ol>
      <p className="font-semibold text-navy pt-1">What is important?</p>
      <ul className="list-disc pl-4 space-y-1">
        <li>F&O data of FII, Pro, and Client is important.</li>
        <li>DII data is not important.</li>
        <li>Stock / Cash data is not important.</li>
        <li>Usually FIIs and Pro are right. Usually Client is wrong.</li>
      </ul>
      <ol className="list-decimal pl-4 space-y-1">
        <li>Options — Useful in predicting direction in the short term for F&O trading.</li>
        <li>Futures — Useful in predicting direction in the short term for F&O trading.</li>
        <li>Cash / Stock data — Not useful for F&O trading.</li>
      </ol>
      <p className="font-semibold text-navy pt-1">Examples</p>
      <ul className="list-disc pl-4 space-y-1">
        <li>If FII is bullish in Options and Futures, and Retail is bearish in Options and Futures, the market is expected to go up.</li>
        <li>If FII is bearish in Options and Futures, and Retail is bullish in Options and Futures, the market is expected to go down.</li>
      </ul>
    </InfoPopover>
  );
}

const PARTICIPANTS = ["FII", "Pro", "Client", "DII"];
const SEGMENTS = ["Index Futures", "Index Options", "Stock Futures", "Stocks"];

function segMatches(rowSeg, selected) {
  const s = (rowSeg || "").toLowerCase();
  return selected.some((sel) => {
    if (sel === "Index Options") return /index/.test(s) && /(call|put) option|options/.test(s);
    if (sel === "Stocks") return s === "stocks";
    return rowSeg === sel;
  });
}

function fmt(v) {
  if (v === null || v === undefined) return "—";
  const a = Math.abs(v);
  const s = v < 0 ? "-" : "";
  if (a >= 100000) return `${s}${(a / 100000).toFixed(2)}L`;
  if (a >= 1000) return `${s}${Math.round(a).toLocaleString("en-IN")}`;
  return `${s}${Math.round(a)}`;
}

function Bar({ label, direction }) {
  if (!direction) return <div className="text-center text-xs text-slate-400 italic">{label}</div>;
  const bull = direction > 0;
  const pct = label.startsWith("Strong") ? 50 : label.startsWith("Medium") ? 34 : 20;
  return (
    <div className="relative w-full min-w-[240px] h-6">
      <div className="absolute left-1/2 top-0 bottom-0 w-px bg-slate-300 -translate-x-1/2" />
      {!bull && (
        <div style={{ width: `${pct}%` }} className="absolute right-1/2 top-1/2 -translate-y-1/2 h-5 flex items-center justify-end bg-red-100 text-red-800 rounded-l px-2 text-xs font-semibold whitespace-nowrap">{label}</div>
      )}
      {bull && (
        <div style={{ width: `${pct}%` }} className="absolute left-1/2 top-1/2 -translate-y-1/2 h-5 flex items-center justify-start bg-emerald-100 text-emerald-800 rounded-r px-2 text-xs font-semibold whitespace-nowrap">{label}</div>
      )}
    </div>
  );
}

function SidePanel({ summary, updatedAt }) {
  const Row = ({ label, val, testid }) => (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0 gap-3">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-navy text-right" data-testid={testid}>{val ?? "—"}</span>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="oi-side-panel">
      <div className="border border-border rounded-lg p-5 bg-white">
        <p className="eyebrow text-gold mb-3">Market Snapshot (NIFTY)</p>
        <Row label="OI PCR" val={summary.oi_pcr} testid="oi-pcr" />
        <Row label="OI Support (PE)" val={summary.oi_support} testid="oi-support" />
        <Row label="OI Resistance (CE)" val={summary.oi_resistance} testid="oi-resistance" />
        <Row label="CPR Width" val={summary.cpr_width} testid="oi-cpr-width" />
        <Row label="CPR Type" val={summary.cpr_type} testid="oi-cpr-type" />
        <Row label="Market Structure" val={summary.market_structure} testid="oi-market-structure" />
      </div>
      <div className="border border-border rounded-lg p-5 bg-white">
        <p className="eyebrow text-gold mb-2">Index Quotes</p>
        <Row label="NIFTY" val={summary.nifty} testid="oi-nifty" />
        <Row label="BANKNIFTY" val={summary.banknifty} testid="oi-banknifty" />
        <Row label="SENSEX" val={summary.sensex} testid="oi-sensex" />
      </div>
      {updatedAt && <p className="text-xs text-slate-400 text-center">Updated · {new Date(updatedAt).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</p>}
    </div>
  );
}

export default function ParticipantOI() {
  const [sessions, setSessions] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [sel, setSel] = useState(0);
  const [parts, setParts] = useState(["FII", "Pro", "Client"]);
  const [segs, setSegs] = useState(["Index Futures", "Index Options"]);
  const [showLabels, setShowLabels] = useState(true);
  const [dateOpen, setDateOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = () => api.get("/oi/latest").then((r) => {
      if (!alive) return;
      setSessions(r.data.sessions || []);
      setUpdatedAt(r.data.updated_at);
    }).catch(() => {});
    load();
    const iv = setInterval(load, 120000); // auto-refresh every 2 min
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { alive = false; clearInterval(iv); window.removeEventListener("focus", onFocus); };
  }, []);

  const current = sessions[sel] || { rows: [], summary: {}, label: null };
  const prevMap = useMemo(() => {
    const p = sessions[sel + 1];
    const m = {};
    if (p) p.rows.forEach((r) => { m[`${r.participant}|${r.segment}`] = r.direction; });
    return m;
  }, [sessions, sel]);

  const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const grouped = useMemo(() => {
    const rows = (current.rows || []).filter((r) => parts.includes(r.participant) && segMatches(r.segment, segs))
      .map((r) => {
        const prevDir = prevMap[`${r.participant}|${r.segment}`];
        const flipped = prevDir !== undefined && r.direction !== 0 && prevDir !== 0 && Math.sign(prevDir) !== Math.sign(r.direction);
        return { ...r, flipped };
      });
    const g = {};
    rows.forEach((r) => { (g[r.participant] = g[r.participant] || []).push(r); });
    return PARTICIPANTS.filter((p) => g[p]).map((p) => [p, g[p]]);
  }, [current, parts, segs, prevMap]);

  return (
    <div data-testid="participant-oi-page">
      <PageHero eyebrow="Market Data" title="Participant-Wise OI" subtitle="End-of-day net Open Interest positioning of FII, DII, Pro and Client across index and stock segments. Educational data, not investment advice." crumbs={[{ label: "Participant OI" }]} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-[220px_1fr_260px] gap-8">
        <aside className="space-y-6" data-testid="oi-filters">
          {sessions.length > 0 && (() => {
            const toISO = (lbl) => { const m = /(\d{2})\.(\d{2})\.(\d{4})/.exec(lbl || ""); return m ? `${m[3]}-${m[2]}-${m[1]}` : ""; };
            const dISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            const dateMap = {};
            sessions.forEach((s, i) => { const iso = toISO(s.label); if (iso) dateMap[iso] = i; });
            const isoDates = Object.keys(dateMap).sort();
            const isoSet = new Set(isoDates);
            const parse = (iso) => { const [y, m, d] = iso.split("-").map(Number); return new Date(y, m - 1, d); };
            const selIso = toISO(sessions[sel]?.label);
            const selDate = selIso ? parse(selIso) : undefined;
            const label = selDate ? selDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "Select date";
            return (
              <div>
                <h3 className="font-display font-semibold text-navy mb-2">Select Date</h3>
                <Popover open={dateOpen} onOpenChange={setDateOpen}>
                  <PopoverTrigger asChild>
                    <button data-testid="oi-date-select" className="w-full h-10 rounded-md border border-border px-3 text-sm flex items-center justify-between gap-2 bg-white hover:border-gold transition-colors focus:outline-none focus:ring-2 focus:ring-gold">
                      <span className="text-navy font-medium">{label}</span>
                      <CalendarIcon className="h-4 w-4 text-gold" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={selDate}
                      defaultMonth={selDate}
                      onSelect={(d) => { if (!d) return; const iso = dISO(d); if (dateMap[iso] !== undefined) { setSel(dateMap[iso]); setDateOpen(false); } }}
                      disabled={(d) => !isoSet.has(dISO(d))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            );
          })()}
          <div>
            <h3 className="font-display font-semibold text-navy mb-3">Participant</h3>
            {PARTICIPANTS.map((p) => (
              <label key={p} className="flex items-center gap-2 py-1 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={parts.includes(p)} onChange={() => toggle(parts, setParts, p)} data-testid={`filter-part-${p}`} className="accent-navy" /> {p}
              </label>
            ))}
          </div>
          <div>
            <h3 className="font-display font-semibold text-navy mb-3">Segment</h3>
            {SEGMENTS.map((s) => (
              <label key={s} className="flex items-center gap-2 py-1 text-sm text-slate-600 cursor-pointer">
                <input type="checkbox" checked={segs.includes(s)} onChange={() => toggle(segs, setSegs, s)} className="accent-navy" /> {s}
              </label>
            ))}
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input type="checkbox" checked={showLabels} onChange={() => setShowLabels((v) => !v)} className="accent-gold" /> Show Labels
          </label>
          <p className="flex items-center gap-1.5 text-xs text-amber-600"><ArrowLeftRight className="h-3.5 w-3.5" /> Amber row = flipped vs previous session</p>
        </aside>

        <div className="overflow-x-auto border border-border rounded-lg bg-white" data-testid="oi-table">
          <table className="w-full text-sm">
            <thead className="bg-subtle text-xs text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">Participant<ParticipantInfo /></th>
                <th className="px-4 py-3 text-left font-medium">Segment</th>
                <th className="px-4 py-3 text-center font-medium whitespace-nowrap">Bearish / Bullish<BiasInfo /></th>
                <th className="px-4 py-3 text-right font-medium">Net OI</th>
                <th className="px-4 py-3 text-right font-medium">Change</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {grouped.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No OI data available yet. It updates after your daily upload.</td></tr>}
              {grouped.map(([p, items]) => items.map((r, i) => (
                <tr key={p + r.segment} data-testid={`oi-row-${p}-${r.segment}`.replace(/\s/g, "-")} className={r.flipped ? "bg-amber-50" : ""}>
                  <td className="px-4 py-3 font-semibold text-navy align-top">{i === 0 ? p : ""}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {r.segment}
                    {r.flipped && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">Flipped</span>}
                  </td>
                  <td className="px-4 py-3">{showLabels ? <Bar label={r.label} direction={r.direction} /> : <span className="text-slate-300">—</span>}</td>
                  <td className={`px-4 py-3 text-right font-medium ${r.net_oi < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmt(r.net_oi)}</td>
                  <td className={`px-4 py-3 text-right ${r.change < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmt(r.change)}</td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>

        <SidePanel summary={current.summary || {}} updatedAt={updatedAt} />
      </div>
      {current.table && current.table.length > 0 && (() => {
        const HEAD = ["Segment", "Buy/Sell", "Today's OI Change", "Today's OI Change (%)", "Today's OI (No. of Contracts)", "Today's OI Share (%)", "Confirmation"];
        const isHeaderRow = (r) => (r[1] || "").toLowerCase() === "buy/sell";
        const isBanner = (r) => r[0] && !r[1] && !r[2] && !r[3] && !r[4] && !r[5] && !r[6];
        const partOf = (t) => {
          t = (t || "").toLowerCase().replace(/^[^a-z]+/, "");
          return t.startsWith("fii") ? "FII" : t.startsWith("pro") ? "Pro" : t.startsWith("client") ? "Client" : t.startsWith("dii") ? "DII" : null;
        };
        const PAD = (i) => i === 0 ? "pl-3 pr-1" : i === 1 ? "pl-1 pr-6" : i >= 2 && i <= 4 ? "px-6" : i === 5 ? "pl-6 pr-1" : "pl-1 pr-3";
        const body = [];
        let cur = null, curSeg = null;
        for (const r of current.table) {
          if (isHeaderRow(r)) continue;
          if (isBanner(r)) {
            const p = partOf(r[0]);
            if (p) { cur = p; curSeg = null; if (parts.includes(p)) body.push(r); }
            else if (cur && parts.includes(cur)) body.push(r);
            continue;
          }
          const bs = (r[1] || "").toLowerCase();
          if (bs.startsWith("net")) continue;
          if (r.join(" ").toLowerCase().includes("pcr")) continue;
          if (r[0]) curSeg = r[0];
          if (!(cur && parts.includes(cur))) continue;
          if (!segMatches(curSeg, segs)) continue;
          body.push(r);
        }
        return (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6" data-testid="oi-detail-section">
            <h2 className="font-display text-lg font-semibold text-navy mb-1">Detailed Participant OI Breakdown</h2>
            <p className="text-xs text-slate-400 mb-3">Filtered by the Participant, Segment and Date selected above.</p>
            <div className="overflow-x-auto border border-border rounded-lg bg-white">
              <table className="w-full text-sm" data-testid="oi-detail-table">
                <thead className="bg-subtle text-xs text-slate-500">
                  <tr>{HEAD.map((h, i) => <th key={i} className={`${PAD(i)} py-2 font-medium ${i === 0 ? "text-left" : "text-right"} ${i >= 2 && i <= 5 ? "whitespace-normal w-20" : "whitespace-nowrap"}`}>{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {body.map((r, ri) => isBanner(r) ? (
                    <tr key={ri} className="bg-navy/5"><td colSpan={7} className="px-3 py-2 font-semibold text-navy text-xs text-center">{r[0]}</td></tr>
                  ) : (
                    <tr key={ri}>{r.map((c, ci) => {
                      let cls = ci === 0 ? "font-medium text-navy text-left" : "text-slate-600 text-right";
                      if (ci === 6 && c) {
                        const t = c.toLowerCase();
                        if (t.includes("long buildup") || t.includes("short covering")) cls = "text-emerald-700 font-semibold text-right";
                        else if (t.includes("short buildup") || t.includes("long unwinding")) cls = "text-red-600 font-semibold text-right";
                      }
                      return <td key={ci} className={`${PAD(ci)} py-2 whitespace-nowrap ${cls}`}>{c}</td>;
                    })}</tr>
                  ))}
                  {body.filter((r) => !isBanner(r)).length === 0 && (
                    <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No rows for the selected participants / segments.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14">
        <Disclaimer />
      </div>
    </div>
  );
}
